import { z } from 'zod';
import { pageSchema, resourceKeySchema, timestampSchema, versionedSchema } from './common';

/** A calendar date, no time and no zone. Local to this module, matching
 *  `costing.ts` -- a date on a registration document is a date, not an instant. */
const isoDateSchema = z.string().date();

/**
 * The clean-vehicle fleet: vehicles, framework agreements, and the declarations
 * that connect them (docs/clean-vehicles-design.md).
 *
 * ## Three records, because they are three facts
 *
 * A **vehicle** is a physical thing an organization holds a record of. A
 * **framework agreement** is one procurement by one of an authority's
 * procurement units from one supplier. An **assignment** is the declaration
 * that this vehicle served that agreement over a period -- and it is the
 * interesting one, because a vehicle may serve several agreements at once and
 * the directive counts it under each.
 *
 * ## Nothing here says whether a vehicle is clean
 *
 * That question has no answer without a date. See `fleet-compliance.ts`: the
 * rules changed on 1 January 2026, so a lorry unchanged in every respect counts
 * under one reference period and not the next. A stored boolean would make
 * every historical report silently wrong the morning the rules changed, which
 * is why there is no `isClean` column anywhere in this file.
 *
 * ## The assignment carries a copy of the vehicle, deliberately
 *
 * `VehicleFacts` is repeated on the assignment rather than joined from the
 * vehicle. Two reasons, and the second is the one that decides it.
 *
 * A compliance report is a statement about what was declared for a procurement,
 * not about what the fleet looks like today. When an operator corrects a CO2
 * figure, an authority's report for a period that has already been filed must
 * not change underneath it.
 *
 * And it is what lets an operator's own organization hold the vehicle while the
 * authority holds the agreement (design section 5.8). The authority reads its
 * own rows; nothing on its read path reaches into another tenant. The single
 * crossing is the *write* that makes the declaration, which is rare, consented
 * to, and audited.
 */

/* ------------------------------------------------------------- vocabulary */

/**
 * The EU vehicle categories the directive is written in terms of.
 *
 * Not a free-text field and not this product's own vocabulary: the targets are
 * stated per category group in the Annex, so a value outside this list is a row
 * the report cannot place. M1 is a car, M2/M3 buses and coaches by mass, N1 a
 * van, N2/N3 lorries by mass.
 */
export const VEHICLE_CATEGORIES = ['M1', 'M2', 'M3', 'N1', 'N2', 'N3'] as const;
export const vehicleCategorySchema = z.enum(VEHICLE_CATEGORIES);
export type VehicleCategory = (typeof VEHICLE_CATEGORIES)[number];

/**
 * The three groups the targets are set for.
 *
 * Derived from the category rather than stored beside it, because it is a
 * property of the category and storing both invites them to disagree.
 */
export const CATEGORY_GROUPS = ['light_duty', 'trucks', 'buses'] as const;
export const categoryGroupSchema = z.enum(CATEGORY_GROUPS);
export type CategoryGroup = (typeof CATEGORY_GROUPS)[number];

const CATEGORY_GROUP_OF: Record<VehicleCategory, CategoryGroup> = {
  M1: 'light_duty',
  M2: 'light_duty',
  N1: 'light_duty',
  M3: 'buses',
  N2: 'trucks',
  N3: 'trucks',
};

export function categoryGroupOf(category: VehicleCategory): CategoryGroup {
  return CATEGORY_GROUP_OF[category];
}

/**
 * What the vehicle runs on.
 *
 * A list rather than one value, because a plug-in hybrid genuinely has two and
 * a single column would make somebody choose one and lose the half that decides
 * the answer.
 *
 * `zeroEmission` and `alternative` are properties of the fuel, declared here
 * once, so the compliance rules can ask "is any of these zero-emission?"
 * without a second table to keep in step. `alternative` follows the alternative
 * fuels of Directive 2014/94/EU, which is what the heavy-duty test reads --
 * with the ILUC exclusion carried separately on `highIlucRisk`, because
 * whether a biofuel counts depends on its feedstock rather than on its name.
 */
export interface EnergySourceDefinition {
  key: string;
  labelKey: string;
  zeroEmission: boolean;
  alternative: boolean;
  /** Set where the fuel may be produced from high-ILUC-risk feedstock, which
   *  the heavy-duty test excludes. Declaring it needs the feedstock, which this
   *  product does not collect -- so a rule that cares returns `indeterminate`
   *  rather than guessing. */
  ilucSensitive: boolean;
  order: number;
}

export const ENERGY_SOURCE_CATALOG = {
  electricity: {
    key: 'electricity',
    labelKey: 'fleet.energy.electricity',
    zeroEmission: true,
    alternative: true,
    ilucSensitive: false,
    order: 10,
  },
  hydrogen: {
    key: 'hydrogen',
    labelKey: 'fleet.energy.hydrogen',
    zeroEmission: true,
    alternative: true,
    ilucSensitive: false,
    order: 20,
  },
  natural_gas: {
    key: 'natural_gas',
    labelKey: 'fleet.energy.naturalGas',
    zeroEmission: false,
    alternative: true,
    ilucSensitive: false,
    order: 30,
  },
  biomethane: {
    key: 'biomethane',
    labelKey: 'fleet.energy.biomethane',
    zeroEmission: false,
    alternative: true,
    ilucSensitive: false,
    order: 40,
  },
  lpg: {
    key: 'lpg',
    labelKey: 'fleet.energy.lpg',
    zeroEmission: false,
    alternative: true,
    ilucSensitive: false,
    order: 50,
  },
  biofuel: {
    key: 'biofuel',
    labelKey: 'fleet.energy.biofuel',
    zeroEmission: false,
    alternative: true,
    ilucSensitive: true,
    order: 60,
  },
  synthetic_fuel: {
    key: 'synthetic_fuel',
    labelKey: 'fleet.energy.syntheticFuel',
    zeroEmission: false,
    alternative: true,
    ilucSensitive: false,
    order: 70,
  },
  petrol: {
    key: 'petrol',
    labelKey: 'fleet.energy.petrol',
    zeroEmission: false,
    alternative: false,
    ilucSensitive: false,
    order: 80,
  },
  diesel: {
    key: 'diesel',
    labelKey: 'fleet.energy.diesel',
    zeroEmission: false,
    alternative: false,
    ilucSensitive: false,
    order: 90,
  },
} as const satisfies Record<string, EnergySourceDefinition>;

export type EnergySource = keyof typeof ENERGY_SOURCE_CATALOG;
export const ENERGY_SOURCES = Object.keys(ENERGY_SOURCE_CATALOG) as [
  EnergySource,
  ...EnergySource[],
];
export const energySourceSchema = z.enum(ENERGY_SOURCES);

export function energySourceDefinition(key: EnergySource): EnergySourceDefinition {
  return ENERGY_SOURCE_CATALOG[key];
}

/**
 * Euro emission classes.
 *
 * Recorded because the client asked for it and because it is what a vehicle's
 * registration document quotes -- not because the directive's clean test reads
 * it. The light-duty test reads CO2 and the RDE share; the heavy-duty test
 * reads the fuel. The class is here for the reader, and for the day a national
 * rule stricter than the directive does read it.
 */
export const EMISSION_CLASSES = [
  'euro_4',
  'euro_5',
  'euro_6',
  'euro_6d',
  'euro_7',
  'euro_iv',
  'euro_v',
  'euro_vi',
  'euro_vii',
] as const;
export const emissionClassSchema = z.enum(EMISSION_CLASSES);
export type EmissionClass = (typeof EMISSION_CLASSES)[number];

/** How a vehicle record got here. Provenance, on the same reasoning as
 *  `CostEntry.supplierLabel`: what the source said is a different fact from what
 *  the authority concluded. */
export const VEHICLE_ORIGINS = ['manual', 'import', 'register_lookup'] as const;
export const vehicleOriginSchema = z.enum(VEHICLE_ORIGINS);
export type VehicleOrigin = (typeof VEHICLE_ORIGINS)[number];

/** Where an agreement is in its own life. Three, on the same reasoning as a
 *  contract's three: negotiated, awarded, finished. */
export const AGREEMENT_STATUSES = ['draft', 'active', 'closed'] as const;
export const agreementStatusSchema = z.enum(AGREEMENT_STATUSES);
export type AgreementStatus = (typeof AGREEMENT_STATUSES)[number];

/** What the authority buys. Decides scope together with the CPV codes: a
 *  service contract counts only under the listed codes, a purchase always. */
export const PROCUREMENT_KINDS = ['purchase', 'lease', 'rent', 'hire_purchase', 'service'] as const;
export const procurementKindSchema = z.enum(PROCUREMENT_KINDS);
export type ProcurementKind = (typeof PROCUREMENT_KINDS)[number];

/**
 * How far a declaration has been checked.
 *
 * `disputed` rather than `rejected`, and the row survives either way: deleting
 * a supplier's statement would destroy the record of what was claimed, which is
 * the thing an audit asks for first.
 */
export const VERIFICATION_STATES = ['declared', 'verified', 'disputed'] as const;
export const verificationStateSchema = z.enum(VERIFICATION_STATES);
export type VerificationState = (typeof VERIFICATION_STATES)[number];

/* ------------------------------------------------------------- the facts */

/**
 * Everything the compliance rules read about a vehicle.
 *
 * Extracted as its own schema because it appears twice: on the vehicle, where
 * it is maintained, and on the assignment, where it is the copy that was
 * declared. One definition means the two cannot drift, and `evaluate` takes
 * this rather than either of them -- so the rules never learn which they were
 * handed.
 */
export const vehicleFactsSchema = z.object({
  category: vehicleCategorySchema,
  energySources: z.array(energySourceSchema),
  emissionClass: emissionClassSchema.nullable(),
  /** WLTP combined, g/km. The light-duty test's input. */
  co2WltpGramsPerKm: z.number().int().nonnegative().nullable(),
  /** g/kWh. What a zero-emission heavy-duty vehicle is measured against. */
  co2GramsPerKwh: z.number().int().nonnegative().nullable(),
  /** The vehicle's real-driving emissions as a percentage of the applicable
   *  limit. The first reference period's second light-duty condition. */
  maxRdeNoxPercent: z.number().int().nonnegative().nullable(),
});
export type VehicleFacts = z.infer<typeof vehicleFactsSchema>;

/**
 * The facts the compliance rules read, from anything that carries them.
 *
 * A vehicle, a declaration, and a half-filled form all carry the same six
 * fields, and all three were mapping them by hand -- in the API service, in the
 * register screen and in the vehicle form. Three copies of one projection is
 * three places to forget a field the day the rules read a seventh, and the
 * symptom would be a verdict that differs between the form and the report it
 * feeds.
 *
 * `energySources` is widened rather than validated here: the caller has already
 * been through `vehicleSchema` or the database enum, and re-parsing on every
 * row of a report is work for no answer.
 */
export function vehicleFactsOf(row: {
  category: VehicleCategory;
  energySources: readonly string[];
  emissionClass: EmissionClass | null;
  co2WltpGramsPerKm: number | null;
  co2GramsPerKwh: number | null;
  maxRdeNoxPercent: number | null;
}): VehicleFacts {
  return {
    category: row.category,
    energySources: row.energySources as EnergySource[],
    emissionClass: row.emissionClass,
    co2WltpGramsPerKm: row.co2WltpGramsPerKm,
    co2GramsPerKwh: row.co2GramsPerKwh,
    maxRdeNoxPercent: row.maxRdeNoxPercent,
  };
}

/* ------------------------------------------------------------- vehicles */

/**
 * A plate as people write it, and the form the duplicate report groups by.
 *
 * Normalization is upper-case with every non-alphanumeric character dropped, so
 * `ABC-123`, `abc 123` and `ABC123` are one vehicle. Exported because the
 * browser needs the same answer as the API when it warns about a collision
 * before the save.
 */
export function normalizePlate(plate: string): string {
  return plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export const registrationPlateSchema = z
  .string()
  .trim()
  .min(1)
  .max(16)
  .refine((value) => normalizePlate(value).length > 0, 'must contain a letter or a digit');

/** 17 characters, excluding I, O and Q, which the standard omits so they cannot
 *  be confused with 1 and 0. Optional everywhere: plenty of real records have
 *  no VIN, and refusing them would lose the vehicle rather than gain the code. */
export const vinSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(17)
  .regex(/^[A-HJ-NPR-Z0-9]{17}$/, 'is not a valid vehicle identification number');

const vehicleWritableFields = z.object({
  registrationPlate: registrationPlateSchema,
  vin: vinSchema.nullish(),
  category: vehicleCategorySchema,
  /** At least one: a vehicle that runs on nothing is a data-entry error, and
   *  accepting it would put an `indeterminate` in the report for a reason
   *  nobody could act on. */
  energySources: z.array(energySourceSchema).min(1).max(4),
  emissionClass: emissionClassSchema.nullish(),
  co2WltpGramsPerKm: z.number().int().nonnegative().max(2000).nullish(),
  co2GramsPerKwh: z.number().int().nonnegative().max(5000).nullish(),
  maxRdeNoxPercent: z.number().int().nonnegative().max(1000).nullish(),
  firstRegisteredOn: isoDateSchema.nullish(),
  withdrawnOn: isoDateSchema.nullish(),
  /** The counterparty this vehicle belongs to, when this organization holds the
   *  record on their behalf. Null when the organization operates it itself. */
  partyId: z.string().uuid().nullish(),
  notes: z.string().max(2000).nullish(),
});

export const vehicleSchema = versionedSchema.extend({
  id: z.string().uuid(),
  registrationPlate: z.string(),
  plateNormalized: z.string(),
  vin: z.string().nullable(),
  category: vehicleCategorySchema,
  energySources: z.array(energySourceSchema),
  emissionClass: emissionClassSchema.nullable(),
  co2WltpGramsPerKm: z.number().int().nullable(),
  co2GramsPerKwh: z.number().int().nullable(),
  maxRdeNoxPercent: z.number().int().nullable(),
  firstRegisteredOn: isoDateSchema.nullable(),
  withdrawnOn: isoDateSchema.nullable(),
  party: z.object({ id: z.string().uuid(), key: resourceKeySchema, name: z.string() }).nullable(),
  origin: vehicleOriginSchema,
  /** The declaring organization's dated statement that this record is current.
   *  Null until somebody presses the button once. */
  attestedAt: timestampSchema.nullable(),
  notes: z.string().nullable(),
  /** How many live assignments name this vehicle, so deleting one is an
   *  informed act rather than a surprise. */
  assignmentCount: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Vehicle = z.infer<typeof vehicleSchema>;

export const createVehicleSchema = vehicleWritableFields.strict();
export type CreateVehicle = z.infer<typeof createVehicleSchema>;

export const updateVehicleSchema = vehicleWritableFields
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateVehicle = z.infer<typeof updateVehicleSchema>;

export const vehicleListQuerySchema = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  category: vehicleCategorySchema.optional(),
  energySource: energySourceSchema.optional(),
  partyId: z.string().uuid().optional(),
  /** Free text over the plate and the VIN. Normalized before comparison, so a
   *  reader may type the plate however they have it written down. */
  search: z.string().trim().max(64).optional(),
  includeWithdrawn: z.coerce.boolean().optional(),
});
export type VehicleListQuery = z.infer<typeof vehicleListQuerySchema>;

/*
 * There is deliberately no `status` or `asOf` filter here.
 *
 * Whether a vehicle is clean is a function of the rules in force on a date, so
 * there is no column to filter on and no `where` clause that expresses it. An
 * earlier version accepted both and applied them to the page *after* it had
 * been fetched -- which narrows the page rather than searching past it, so a
 * caller asking for undetermined vehicles could be told there are none while
 * fifty sit on page two. A filter that answers "no matches" when there are
 * matches is worse than no filter.
 *
 * The register screen filters by verdict correctly by fetching the whole list
 * and judging it (`useAllFleetVehicles`), which is bounded work on a register
 * of hundreds. The day it stops being bounded, the answer is a stored
 * projection refreshed when the rules change -- not this.
 */

/* ------------------------------------------------------ procurement units */

export const procurementUnitSchema = versionedSchema.extend({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  code: z.string().nullable(),
  parentId: z.string().uuid().nullable(),
  agreementCount: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ProcurementUnit = z.infer<typeof procurementUnitSchema>;

const procurementUnitWritableFields = z.object({
  name: z.string().trim().min(1).max(200),
  code: z.string().trim().max(64).nullish(),
  parentId: z.string().uuid().nullish(),
});

export const createProcurementUnitSchema = procurementUnitWritableFields
  .extend({ key: resourceKeySchema })
  .strict();
export type CreateProcurementUnit = z.infer<typeof createProcurementUnitSchema>;

export const updateProcurementUnitSchema = procurementUnitWritableFields
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateProcurementUnit = z.infer<typeof updateProcurementUnitSchema>;

/* ------------------------------------------------------------ agreements */

/**
 * A CPV code as it is written in a contract notice: eight digits, optionally
 * with the check digit after a hyphen. Stored as given; compared on the first
 * eight, because the check digit distinguishes nothing the scope test reads.
 */
export const cpvCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{8}(-\d)?$/, 'must be an eight-digit CPV code, optionally with a check digit');

export function normalizeCpv(code: string): string {
  return code.trim().slice(0, 8);
}

const agreementWritableFields = z.object({
  title: z.string().trim().min(1).max(200),
  reference: z.string().trim().max(120).nullish(),
  procurementUnitId: z.string().uuid(),
  supplierPartyId: z.string().uuid(),
  status: agreementStatusSchema,
  /** The date the contract was awarded, which is what decides the reference
   *  period. Required to leave `draft`; the service enforces that rather than
   *  the schema, so a half-filled draft can still be saved. */
  awardedOn: isoDateSchema.nullish(),
  startsOn: isoDateSchema.nullish(),
  endsOn: isoDateSchema.nullish(),
  optionEndsOn: isoDateSchema.nullish(),
  procurementKind: procurementKindSchema,
  cpvCodes: z.array(cpvCodeSchema).max(20),
  /** Overrides the computed scope determination. Null leaves it computed, which
   *  is what almost every agreement should do. */
  inScopeOverride: z.boolean().nullish(),
  scopeOverrideReason: z.string().trim().max(500).nullish(),
  notes: z.string().max(2000).nullish(),
});

export const frameworkAgreementSchema = versionedSchema.extend({
  id: z.string().uuid(),
  key: resourceKeySchema,
  title: z.string(),
  reference: z.string().nullable(),
  procurementUnit: z.object({ id: z.string().uuid(), key: resourceKeySchema, name: z.string() }),
  supplierParty: z.object({ id: z.string().uuid(), key: resourceKeySchema, name: z.string() }),
  status: agreementStatusSchema,
  awardedOn: isoDateSchema.nullable(),
  startsOn: isoDateSchema.nullable(),
  endsOn: isoDateSchema.nullable(),
  optionEndsOn: isoDateSchema.nullable(),
  procurementKind: procurementKindSchema,
  cpvCodes: z.array(z.string()),
  /** What the scope rules concluded, before any override. Carried so the screen
   *  can show "in scope (CPV 90511000)" or "out of scope, overridden" rather
   *  than a bare boolean nobody can argue with. */
  inScopeComputed: z.boolean(),
  inScopeOverride: z.boolean().nullable(),
  scopeOverrideReason: z.string().nullable(),
  /** The effective answer: the override where there is one, else the computed. */
  inScope: z.boolean(),
  /** Which reference period `awardedOn` falls in, or null when it falls outside
   *  every period -- an agreement awarded before the directive applied. */
  referencePeriodId: z.string().nullable(),
  notes: z.string().nullable(),
  assignmentCount: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type FrameworkAgreement = z.infer<typeof frameworkAgreementSchema>;

export const createAgreementSchema = agreementWritableFields
  .extend({ key: resourceKeySchema })
  .partial({ status: true })
  .strict();
export type CreateAgreement = z.infer<typeof createAgreementSchema>;

export const updateAgreementSchema = agreementWritableFields
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateAgreement = z.infer<typeof updateAgreementSchema>;

export const agreementListQuerySchema = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  status: agreementStatusSchema.optional(),
  procurementUnitId: z.string().uuid().optional(),
  supplierPartyId: z.string().uuid().optional(),
  referencePeriodId: z.string().max(32).optional(),
  search: z.string().trim().max(64).optional(),
  /**
   * Agreements whose term runs out between today and this date.
   *
   * "Term" means the option end where there is one and the end date otherwise:
   * an agreement with an option genuinely runs that long, and listing it as
   * expiring the day its base term ends would send somebody to retender a
   * contract that has two more years.
   *
   * A filter rather than something the caller sifts a page for. The expiry tile
   * used to fetch a hundred agreements and narrow them in the browser, which
   * quietly missed any that sorted past the hundredth -- and the one it missed
   * would be the one nobody had noticed was ending.
   */
  expiringBefore: isoDateSchema.optional(),
});
export type AgreementListQuery = z.infer<typeof agreementListQuerySchema>;

/* ----------------------------------------------------------- assignments */

export const fleetAssignmentSchema = versionedSchema.extend({
  id: z.string().uuid(),
  agreementId: z.string().uuid(),
  agreementTitle: z.string(),
  /** The vehicle this was declared from. In the shared arrangement the vehicle
   *  lives in the declaring organization, so this is an identifier rather than
   *  a relation -- the facts below are the copy that was disclosed. */
  sourceVehicleId: z.string().uuid(),
  sourceOrganizationId: z.string().uuid(),
  registrationPlate: z.string(),
  plateNormalized: z.string(),
  vin: z.string().nullable(),
  facts: vehicleFactsSchema,
  firstRegisteredOn: isoDateSchema.nullable(),
  startsOn: isoDateSchema,
  endsOn: isoDateSchema.nullable(),
  verification: verificationStateSchema,
  verificationNote: z.string().nullable(),
  verifiedAt: timestampSchema.nullable(),
  declaredAt: timestampSchema,
  /** Set when a declaration is taken back. The row survives, excluded from
   *  every report, because what was claimed is the fact an audit asks for. */
  withdrawnAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type FleetAssignment = z.infer<typeof fleetAssignmentSchema>;

export const createAssignmentSchema = z
  .object({
    agreementId: z.string().uuid(),
    vehicleId: z.string().uuid(),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema.nullish(),
  })
  .strict()
  .refine((value) => value.endsOn == null || value.endsOn >= value.startsOn, {
    message: 'endsOn must not precede startsOn',
    path: ['endsOn'],
  });
export type CreateAssignment = z.infer<typeof createAssignmentSchema>;

export const updateAssignmentSchema = z
  .object({
    startsOn: isoDateSchema.optional(),
    endsOn: isoDateSchema.nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateAssignment = z.infer<typeof updateAssignmentSchema>;

export const verifyAssignmentSchema = z
  .object({
    verification: z.enum(['verified', 'disputed']),
    note: z.string().trim().max(500).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict()
  .refine((value) => value.verification !== 'disputed' || (value.note ?? '').length > 0, {
    message: 'a disputed declaration needs a note saying what is wrong with it',
    path: ['note'],
  });
export type VerifyAssignment = z.infer<typeof verifyAssignmentSchema>;

export const assignmentListQuerySchema = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  agreementId: z.string().uuid().optional(),
  vehicleId: z.string().uuid().optional(),
  verification: verificationStateSchema.optional(),
  includeWithdrawn: z.coerce.boolean().optional(),
});
export type AssignmentListQuery = z.infer<typeof assignmentListQuerySchema>;

/* --------------------------------------------------------- data quality */

/**
 * What is wrong with the register, in the order somebody should fix it.
 *
 * Its own endpoint rather than a mode of the list, on the same reasoning as the
 * register's summary: it answers a different question and returns a different
 * shape, and deriving it from the list would download every vehicle to render
 * four numbers.
 */
export const fleetDataQualitySchema = z.object({
  /** Assignments whose declared facts the rules cannot judge, so they fall out
   *  of the numerator and stay in nobody's denominator. */
  indeterminate: z.number().int().nonnegative(),
  /** Plates declared by more than one supplier onto the same agreement. Either
   *  a subcontract, a typo, or double counting -- and all three are the
   *  authority's to resolve, not a save button's. */
  duplicatePlates: z.number().int().nonnegative(),
  /** Vehicles whose record nobody has confirmed inside the attestation
   *  interval. */
  staleAttestations: z.number().int().nonnegative(),
  /** Agreements past `draft` with no award date, so they belong to no reference
   *  period and are counted nowhere. */
  agreementsWithoutAwardDate: z.number().int().nonnegative(),
  /** Agreements ending within ninety days, option included. */
  expiringSoon: z.number().int().nonnegative(),
});
export type FleetDataQuality = z.infer<typeof fleetDataQualitySchema>;

export const duplicatePlateSchema = z.object({
  plateNormalized: z.string(),
  agreementId: z.string().uuid(),
  agreementTitle: z.string(),
  declarations: z.array(
    z.object({
      assignmentId: z.string().uuid(),
      registrationPlate: z.string(),
      supplierName: z.string(),
      declaredAt: timestampSchema,
    }),
  ),
});
export type DuplicatePlate = z.infer<typeof duplicatePlateSchema>;

/* -------------------------------------------------------------- summary */

/**
 * The shape of the fleet, for the tiles that draw it.
 *
 * Its own endpoint rather than a mode of the vehicle list, on the same
 * reasoning as the register's summary: a tile that downloads three hundred
 * lorries in order to draw nine bars gets slower as the register gets more
 * interesting, and the answer is nine numbers.
 *
 * Every count here is of *vehicles this organization holds*, not of
 * declarations -- "what are we running on" is a question about the fleet, and
 * counting a vehicle once per agreement would answer a different one.
 */
export const fleetSummarySchema = z.object({
  totalVehicles: z.number().int().nonnegative(),
  /** Vehicles with at least one live declaration. The gap between this and
   *  `totalVehicles` is the fleet nobody has told an authority about. */
  declaredVehicles: z.number().int().nonnegative(),
  /**
   * Vehicles per energy source, largest first.
   *
   * A vehicle appears under each of its sources, so these do not sum to
   * `totalVehicles` -- a plug-in hybrid is genuinely both. The chart that draws
   * this must not present it as a share of a whole.
   */
  byEnergySource: z.array(
    z.object({
      source: energySourceSchema,
      vehicles: z.number().int().nonnegative(),
      zeroEmission: z.boolean(),
      alternative: z.boolean(),
    }),
  ),
  byCategoryGroup: z.array(
    z.object({
      categoryGroup: categoryGroupSchema,
      vehicles: z.number().int().nonnegative(),
    }),
  ),
  /** Live declarations by how far they have been checked. Counts declarations
   *  rather than vehicles: verification is an act against a declaration. */
  byVerification: z.object({
    declared: z.number().int().nonnegative(),
    verified: z.number().int().nonnegative(),
    disputed: z.number().int().nonnegative(),
  }),
  /** Vehicles whose record has been confirmed inside the attestation interval,
   *  and the interval itself so a tile can say "within 90 days" in words. */
  attestedVehicles: z.number().int().nonnegative(),
  attestationIntervalDays: z.number().int().positive(),
});
export type FleetSummary = z.infer<typeof fleetSummarySchema>;

/* ------------------------------------------------------------- settings */

/**
 * The fleet settings section.
 *
 * Three fields, and each one is something an administrator changes rather than
 * something an environment variable should decide. The country picks which row
 * of the target catalog applies; the attestation interval decides when a
 * declaration goes stale; the CPV codes decide what a service contract has to
 * quote to be in scope by default.
 */
export const fleetSettingsSchema = z.object({
  /** ISO 3166-1 alpha-2. Which Member State's Annex targets apply. */
  countryCode: z.string().length(2).toUpperCase(),
  attestationIntervalDays: z.number().int().min(7).max(730),
  inScopeCpvCodes: z.array(cpvCodeSchema).max(64),
  version: z.number().int().nonnegative(),
});
export type FleetSettings = z.infer<typeof fleetSettingsSchema>;

export const updateFleetSettingsSchema = fleetSettingsSchema
  .omit({ version: true })
  .partial()
  .extend({ version: z.number().int().nonnegative() })
  .strict();
export type UpdateFleetSettings = z.infer<typeof updateFleetSettingsSchema>;

/**
 * The CPV codes a fresh deployment treats as in scope.
 *
 * The service categories the directive names: public road transport, special
 * purpose and non-scheduled road passenger transport, refuse collection, and
 * mail and parcel transport by road. Seeded as an editable setting rather than
 * hard-coded, because the list is the part most likely to need a lawyer's
 * correction and nobody should need a deploy to apply one.
 */
export const DEFAULT_IN_SCOPE_CPV_CODES = [
  '60112000',
  '60130000',
  '60140000',
  '90511000',
  '60160000',
  '60161000',
  '64121100',
  '64121200',
] as const;

export const DEFAULT_ATTESTATION_INTERVAL_DAYS = 90;

/* --------------------------------------------------------------- pages */

export const vehiclePageSchema = pageSchema(vehicleSchema);
export const frameworkAgreementPageSchema = pageSchema(frameworkAgreementSchema);
export const fleetAssignmentPageSchema = pageSchema(fleetAssignmentSchema);
export const procurementUnitListSchema = z.array(procurementUnitSchema);
export const duplicatePlateListSchema = z.array(duplicatePlateSchema);
export const attestResultSchema = z.object({ attested: z.number().int().nonnegative() });
export const attestRequestSchema = z
  .object({ vehicleIds: z.array(z.string().uuid()).max(1000).default([]) })
  .strict();
